<script lang="ts" setup>
    import { useWindowSize } from '@vueuse/core';
    import closeIcon from '@/assets/icons/close.svg';
    import chevronIcon from '@/assets/icons/chevron-down.svg';
    import vSelect from 'vue-select';
    import FormErrors from '~/storyblok/Form/FormErrors.vue';

    import 'vue-select/dist/vue-select.css';

    type OptionValue = string | number | boolean;

    export type SelectOption = {
        label?: string;
        value: OptionValue;
        selected?: boolean;
        disabled?: boolean;
    };

    interface SelectInputProps {
        label?: string;
        options: SelectOption[];
        required?: boolean;
        readonly disabled?: boolean;
        id?: string;
        class?: string;
        modelValue?: OptionValue;
        validationErrors?: string[];
    }

    const {
        label = '',
        options,
        required = false,
        disabled = false,
        id = '',
        class: _class = '',
        modelValue = undefined,
        validationErrors = []
    } = defineProps<SelectInputProps>();

    const emit = defineEmits<{ (e: 'update:modelValue', value: OptionValue): void }>();

    const BREAKPOINT_WIDTH = 1024;

    const { t } = useI18n();

    const getInitialSelectedOption = (): SelectOption => {
        const predefined = options.find((option) => option.selected);
        if (!options[0])
            throw new Error('[SelectInput.vue] At least one option must be provided via the `options` prop.');
        return predefined || options[0];
    };

    const { width } = useWindowSize();
    // Keep the initial client render equal to the server until hydration completes.
    const windowWidth = ref(Number.POSITIVE_INFINITY);
    const hasFocus = ref(false);

    const resizeHandler = () => {
        windowWidth.value = width.value;
    };

    const selectValue = computed<OptionValue | SelectOption | null>({
        get(): OptionValue | SelectOption | null {
            if (windowWidth.value <= BREAKPOINT_WIDTH) {
                if (!modelValue) return getInitialSelectedOption().value;
                return modelValue ?? null;
            }
            if (!modelValue) return getInitialSelectedOption();
            return options.find((opt) => opt.value === modelValue) ?? null;
        },
        set(newVal) {
            if (windowWidth.value <= BREAKPOINT_WIDTH) {
                emit('update:modelValue', newVal as OptionValue);
            } else if (newVal && typeof newVal === 'object' && 'value' in newVal) {
                emit('update:modelValue', (newVal as SelectOption).value);
            } else {
                emit('update:modelValue', newVal as OptionValue);
            }
        }
    });

    vSelect.props.components.default = () => ({
        Deselect: {
            render() {
                return h('img', {
                    src: closeIcon,
                    ariaHidden: true,
                    role: 'presentation',
                    width: 20,
                    height: 20
                });
            }
        },
        OpenIndicator: {
            render() {
                return h('img', {
                    src: chevronIcon,
                    ariaHidden: true,
                    role: 'presentation',
                    width: 26,
                    height: 26
                });
            }
        }
    });
    const filteredOptions = computed(() => {
        if (windowWidth.value <= BREAKPOINT_WIDTH) {
            return options;
        }

        const [first, ...rest] = options;
        if (first && first.disabled) {
            return rest;
        }

        return options;
    });

    onMounted(() => {
        window.addEventListener('resize', resizeHandler);
        resizeHandler();

        const optionsAriaEl = formRef.value?.querySelector('.vs__dropdown-toggle');
        const clearAriaEl = formRef.value?.querySelector('.vs__clear');
        optionsAriaEl?.setAttribute('aria-label', t('formElement.selectOptions'));
        clearAriaEl?.setAttribute('aria-label', t('formElement.selectClear'));
    });

    onUnmounted(() => {
        window.removeEventListener('resize', resizeHandler);
    });

    const formRef = ref();
    watch(selectValue, (newVal) => {
        if (newVal) emit('update:modelValue', typeof newVal === 'object' && 'value' in newVal ? newVal.value : newVal);
    });

    const uid = id || useId();
    const hiddenInputValue = computed<OptionValue>(() => {
        const val = selectValue.value;
        if (val && typeof val === 'object' && 'value' in val) {
            return val.value;
        }
        return val as OptionValue;
    });

    const isOptionDisabled = computed<boolean>(() => {
        const val = selectValue.value;
        if (val && typeof val === 'object') {
            return Boolean(val.disabled);
        }
        return false;
    });

    const placeholderLabel = computed(() => options.find((el) => el.value === '')?.label || '');
</script>

<template>
    <div
        ref="formRef"
        class="form-element input-wrapper"
        :class="{ '--has-error': validationErrors?.length > 0 }"
    >
        <div class="select-wrapper">
            <label
                v-if="label"
                :for="uid"
            >
                {{ label }}<span v-if="required">*</span>
            </label>

            <div
                v-if="windowWidth <= BREAKPOINT_WIDTH"
                class="select-inner"
            >
                <select
                    :id="uid"
                    v-model="selectValue"
                    :aria-invalid="validationErrors?.length > 0"
                    :aria-describedby="validationErrors?.length > 0 ? `error-${uid}` : undefined"
                    :name="label"
                    :title="options.find((option) => option.value === selectValue)?.label"
                    :required
                    :disabled
                >
                    <option
                        v-for="option in filteredOptions"
                        :key="option.label"
                        :value="option.value"
                        :selected="option.selected"
                        :disabled="'disabled' in option ? !!option.disabled : false"
                    >
                        {{ option.label ?? option.value }}
                    </option>
                </select>

                <img
                    class="chevron"
                    src="~/assets/icons/chevron-down.svg"
                    alt="chevron down"
                    aria-hidden="true"
                />
            </div>

            <ClientOnly v-else>
                <v-select
                    :id="`${uid}-select`"
                    v-model="selectValue"
                    :uid="uid"
                    :input-id="uid"
                    :options="filteredOptions"
                    :class="{ 'has-focus': hasFocus }"
                    :clearable="false"
                    :disabled
                    :placeholder="placeholderLabel"
                    :aria-invalid="validationErrors?.length > 0"
                    :aria-describedby="validationErrors?.length > 0 ? `error-${uid}` : undefined"
                    @search:focus="hasFocus = true"
                    @search:blur="hasFocus = false"
                >
                    <!-- @vue-expect-error vue-select supports #search slot -->
                    <template #search="{ attributes, events }">
                        <input
                            v-bind="attributes"
                            class="vs__search"
                            :aria-labelledby="label ? undefined : attributes['aria-labelledby']"
                            :aria-label="label || undefined"
                            :aria-invalid="validationErrors.length > 0"
                            :aria-describedby="validationErrors.length ? `error-${uid}` : undefined"
                            v-on="events"
                        />
                    </template>
                    <!-- @vue-expect-error vue-select supports #no-options slot -->
                    <template #no-options>{{ t('formElement.noOptionsFound') }}</template>
                </v-select>
                <input
                    type="hidden"
                    :name="label"
                    :value="isOptionDisabled ? '' : hiddenInputValue"
                />
            </ClientOnly>

            <FormErrors
                :id="uid"
                :validation-errors="validationErrors"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/form/form-input.scss';
    @import '~/assets/scss/form/form-select.scss';
</style>
